/**
 * Playground interceptor handlers for the Game Config authoring API.
 * Matches API patterns: /v1/web/game-config/files and /v1/web/game-config/branches
 *
 * A file's branches live in one store keyed by the file, since the tree is only ever read whole.
 */
import { PlaygroundSessionStore } from '../PlaygroundSessionStore.js';
import { contentProblem, isEmptyContent } from '../../pages/game/configs/configContent.js';
import { findDeletedKeys, tryParseJson } from '../../pages/game/configs/editor/jsonDiff.js';
import { rebase } from './gameConfigRebaseMock.js';

const files = new PlaygroundSessionStore('pg_config_files', 40);
const branches = new PlaygroundSessionStore('pg_config_branches', 200);
const now = () => new Date().toISOString();

const body = (config) => (typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {}));
const lineage = (b) => b?.versionInfo?.parentEntityId || b?.id;

const versionInfo = (id, version = 1, description = 'Initial version') => ({
    version,
    parentEntityId: `${id}-parent`,
    isLatest: true,
    versionDescription: description,
    auditInfo: { lastModifiedBy: 'Playground Explorer', lastModifiedDate: now() },
    createdBy: 'Playground Explorer',
    createdDate: now(),
});

/**
 * GET /v1/web/game-config/files — the registry, as a bare array.
 *
 * `includeArchived` INCLUDES the archived files, it does not select them: the real repository drops
 * the `archived != true` clause rather than inverting it. Selecting only archived rows here made the
 * archived view look right by accident while hiding two things the real backend does — the import
 * dedupe reads this with includeArchived to find names already taken (it would have found none), and
 * the area facets are computed over everything the server sent.
 */
export function handleGetConfigFiles(config) {
    const includeArchived = /includeArchived=true/i.test(config.url || '')
        || config.params?.includeArchived === true;
    return files.getAll().filter((f) => includeArchived || !f.archived);
}

/** GET /v1/web/game-config/files/:fileId */
export function handleGetConfigFile(config, match) {
    const item = files.getById(match[1]);
    if (!item) throw new Error(`Config file with id ${match[1]} not found`);
    return item;
}

/**
 * POST /v1/web/game-config/files
 *
 * Scaffolds the base and, unless the file is event-only, a Default branch beneath it — the server
 * does this inside the create, so a caller that added its own base would end up with two. Both start
 * as the request's `content` when there is one, else {}.
 *
 * Refuses what the server refuses before it writes anything: a format other than JSON, and first
 * content that does not parse or holds nothing.
 */
export function handleCreateConfigFile(config) {
    const { content, ...data } = body(config);
    if ((data.format || 'JSON') !== 'JSON') {
        throw new Error('Only JSON config files can be added. Use a .json file.');
    }
    if (content != null) {
        const problem = contentProblem(content);
        if (problem) throw new Error(problem);
    }
    const file = files.create({
        ...data,
        origin: 'MANUAL',
        archived: false,
        createdBy: 'Playground Explorer',
        createdDate: now(),
        lastModifiedBy: 'Playground Explorer',
        lastModifiedDate: now(),
    });
    const seed = content ?? '{}';
    const baseId = PlaygroundSessionStore.getNextId();
    const base = branches.create({
        id: baseId,
        fileId: file.id,
        name: 'Base',
        content: seed,
        isBase: true,
        priority: 0,
        state: 'DRAFT',
        versionInfo: versionInfo(baseId),
    });
    if ((data.delivery || 'ALWAYS_ON') === 'ALWAYS_ON') {
        const defaultId = PlaygroundSessionStore.getNextId();
        branches.create({
            id: defaultId,
            fileId: file.id,
            branchParentId: lineage(base),
            name: 'Default',
            description: 'Everyone in reach - the always-on value when no other branch matches.'
                + ' Publish it to start delivering.',
            content: seed,
            isBase: false,
            priority: 0,
            state: 'DRAFT',
            versionInfo: versionInfo(defaultId),
        });
    }
    return file;
}

/**
 * PUT /v1/web/game-config/files/:fileId — a SPARSE patch, as on the server: a field the caller did
 * not send is left alone, and only an explicitly empty value clears it. Spreading the body wholesale
 * made the dialog look like it could clear a description when the real backend silently kept it.
 */
export function handleUpdateConfigFile(config, match) {
    const patch = Object.fromEntries(Object.entries(body(config)).filter(([, v]) => v !== undefined && v !== null));
    return files.update(match[1], { ...patch, lastModifiedDate: now() });
}

/**
 * POST /v1/web/game-config/files/autofill — a starter file for the description, generated by intel.
 * Nothing is created here; the caller posts the content with the ordinary create.
 *
 * Playground has no model behind it, so it answers with a shape derived from the description rather
 * than pretending to be one — enough to prove the round trip and the "create it blank instead"
 * fallback, which is what the dialog actually branches on.
 */
export function handleAutofillConfigFile(config) {
    const { name, system, description } = body(config);
    if (!String(description || '').trim()) return { content: null };
    return {
        content: `${JSON.stringify({
            _generated: 'playground',
            _from: description,
            enabled: true,
            system: system || 'CORE',
            version: 1,
            entries: [],
        }, null, 2)}\n`,
        name,
    };
}

/** POST /v1/web/game-config/files/:fileId/archive */
export function handleArchiveConfigFile(config, match) {
    const archived = !/archived=false/i.test(config.url || '');
    return files.update(match[1], { archived, lastModifiedDate: now() });
}

/** DELETE /v1/web/game-config/files/:fileId — takes its branches with it. */
export function handleDeleteConfigFile(config, match) {
    const fileId = match[1];
    branches.getAll()
        .filter((b) => b.fileId === fileId)
        .forEach((b) => branches.delete(b.id));
    files.delete(fileId);
    return { success: true };
}

/** GET /v1/web/game-config/files/:fileId/branches */
export function handleGetConfigBranches(config, match) {
    return branches.getAll().filter((b) => b.fileId === match[1]);
}

/** GET /v1/web/game-config/files/:fileId/audience — no overlaps are computed in playground. */
export function handleGetConfigAudience(config, match) {
    return {
        audiences: branches.getAll()
            .filter((b) => b.fileId === match[1])
            .map((b) => ({ branchId: lineage(b), pathBranchIds: [], referencedSegmentIds: b.segmentIds || [] })),
        overlaps: [],
    };
}

/** POST /v1/web/game-config/files/:fileId/branches */
export function handleCreateConfigBranch(config, match) {
    const data = body(config);
    const id = PlaygroundSessionStore.getNextId();
    return branches.create({
        ...data,
        id,
        fileId: match[1],
        isBase: !data.branchParentId,
        state: 'DRAFT',
        versionInfo: versionInfo(id),
    });
}

const byLineage = (parentEntityId) =>
    branches.getAll().find((b) => lineage(b) === parentEntityId);

const requireBranch = (parentEntityId) => {
    const item = byLineage(parentEntityId);
    if (!item) throw new Error(`Config branch ${parentEntityId} not found`);
    return item;
};

/** GET /v1/web/game-config/branches/:parentEntityId */
export function handleGetConfigBranch(config, match) {
    return requireBranch(match[1]);
}

// Descendants of a branch lineage, parents before children.
const descendantsOf = (parentEntityId) => {
    const out = [];
    let frontier = [parentEntityId];
    while (frontier.length) {
        const next = [];
        frontier.forEach((id) => {
            branches.getAll()
                .filter((b) => b.branchParentId === id)
                .forEach((b) => { out.push(b); next.push(lineage(b)); });
        });
        frontier = next;
    }
    return out;
};

// One impact per descendant, each rebased against what its parent will actually serve.
const impactsFor = (saved, pendingContent) => {
    const before = { [lineage(saved)]: saved.content };
    const after = { [lineage(saved)]: pendingContent };
    const names = { [lineage(saved)]: saved.name };
    const chains = { [lineage(saved)]: [] };
    return descendantsOf(lineage(saved)).map((child) => {
        const parentId = child.branchParentId;
        const outcome = rebase(names[parentId] || 'Its parent', before[parentId], after[parentId], child.content);
        const chain = [...(chains[parentId] || []), child.name];
        before[lineage(child)] = child.content;
        // A blocked branch has no merged result, so the branches beneath it are read against what it holds now.
        after[lineage(child)] = outcome.rebasedContent ?? child.content;
        names[lineage(child)] = child.name;
        chains[lineage(child)] = chain;
        return {
            branchId: lineage(child),
            branchName: child.name,
            parentBranchId: parentId,
            ancestorNames: chain,
            classification: outcome.classification,
            reasons: outcome.reasons,
            live: child.state === 'ACTIVE' || child.state === 'PAUSED',
            archived: child.state === 'ARCHIVED',
            flagDefaultsOn: outcome.classification === 'NEEDS_REVIEW' && child.state !== 'ARCHIVED',
            existingReviewNote: child.reviewNote || null,
            currentContent: child.content,
            rebasedContent: outcome.rebasedContent,
            tableTotals: outcome.tableTotals,
        };
    });
};

/** POST /v1/web/game-config/branches/:parentEntityId/save-preview — writes nothing. */
export function handlePreviewConfigSave(config, match) {
    const saved = requireBranch(match[1]);
    const file = files.getById(saved.fileId);
    const structured = (file?.format || 'JSON') === 'JSON';
    const impacts = structured ? impactsFor(saved, body(config).content) : [];
    return {
        branchId: lineage(saved),
        branchName: saved.name,
        structured,
        affectedCount: impacts.length,
        liveCount: impacts.filter((i) => i.live).length,
        blocked: impacts.some((i) => i.classification === 'BLOCKED'),
        impacts,
    };
}

/** POST /v1/web/game-config/branches/:parentEntityId/dismiss-review */
export function handleDismissConfigReview(config, match) {
    const item = requireBranch(match[1]);
    return branches.update(item.id, { reviewNote: null });
}

// A reviewer's value as the server checks it: it parses, and keeps every key its parent will serve.
const reviewedValueProblem = (parentContent, content) => {
    const value = tryParseJson(content);
    if (!value.ok) return 'isn’t valid JSON.';
    const parent = tryParseJson(parentContent);
    const dropped = parent.ok ? findDeletedKeys(parent.value, value.value) : [];
    return dropped.length ? `drops keys its parent has: ${dropped.join(', ')}.` : null;
};

// The walk again with the reviewer's answers, every write checked before any is made, as the server does.
const plannedWrites = (saved, pendingContent, decisions) => {
    const before = { [lineage(saved)]: saved.content };
    const after = { [lineage(saved)]: pendingContent };
    const names = { [lineage(saved)]: saved.name };
    const undecided = [];
    const writes = descendantsOf(lineage(saved)).map((child) => {
        const parentId = child.branchParentId;
        const id = lineage(child);
        const decision = decisions[id];
        const edited = decision?.editedContent != null;
        const outcome = rebase(names[parentId] || 'Its parent', before[parentId], after[parentId], child.content);
        before[id] = child.content;
        names[id] = child.name;
        if (outcome.classification === 'BLOCKED' && !edited) {
            undecided.push(child.name);
            after[id] = child.content;
            return null;
        }
        const problem = edited ? reviewedValueProblem(after[parentId], decision.editedContent) : null;
        if (problem) throw new Error(`The value for “${child.name}” ${problem}`);
        const content = edited ? decision.editedContent : outcome.rebasedContent;
        after[id] = content;
        const keepFlag = decision ? decision.keepFlag : outcome.classification === 'NEEDS_REVIEW';
        return { child, content, reviewNote: keepFlag && child.state !== 'ARCHIVED' ? outcome.reasons.join(' ') : null };
    }).filter(Boolean);
    if (undecided.length) {
        throw new Error(`Still to decide before this can save: ${undecided.join(', ')}. This change can’t be merged into them on its own — give each of them a value in the new shape, then save again.`);
    }
    return writes;
};

/**
 * PUT /v1/web/game-config/branches/:parentEntityId — mints a new version, and carries a content
 * change down to every branch beneath it using the reviewer's decisions.
 */
export function handleSaveConfigBranch(config, match) {
    const item = requireBranch(match[1]);
    const { review, ...rest } = body(config);
    const patch = Object.fromEntries(Object.entries(rest).filter(([, v]) => v != null));
    const decisions = Object.fromEntries((review?.branches || []).map((d) => [d.branchId, d]));
    const writes = patch.content != null && patch.content !== item.content
        ? plannedWrites(item, patch.content, decisions)
        : [];
    writes.forEach(({ child, content, reviewNote }) => {
        branches.update(child.id, {
            content,
            reviewNote,
            versionInfo: {
                ...child.versionInfo,
                version: (child.versionInfo?.version || 1) + 1,
                versionDescription: `Merged from ${item.name}`,
            },
        });
    });
    return branches.update(item.id, {
        ...patch,
        reviewNote: null,
        state: 'DRAFT',
        versionInfo: {
            ...item.versionInfo,
            version: (item.versionInfo?.version || 1) + 1,
            versionDescription: 'Edit',
        },
    });
}

const transition = (parentEntityId, state, description) => {
    const item = requireBranch(parentEntityId);
    return branches.update(item.id, {
        state,
        versionInfo: { ...item.versionInfo, versionDescription: description },
    });
};

// The server refuses to put a branch that holds nothing in front of players; already live is a no-op.
const refuseIfEmpty = (parentEntityId, refusal) => {
    const item = requireBranch(parentEntityId);
    if (item.state !== 'ACTIVE' && isEmptyContent('JSON', item.content)) {
        throw new Error(`"${item.name}" ${refusal}`);
    }
};

export function handlePublishConfigBranch(config, match) {
    refuseIfEmpty(match[1], 'has no content yet, so there is nothing to publish. Add some and save it first.');
    return transition(match[1], 'ACTIVE', 'Published');
}

export function handlePauseConfigBranch(config, match) {
    return transition(match[1], 'PAUSED', 'Paused');
}

export function handleResumeConfigBranch(config, match) {
    refuseIfEmpty(match[1], 'has no content, so resuming it would deliver nothing. Edit it and publish the new version instead.');
    return transition(match[1], 'ACTIVE', 'Resumed');
}

/** POST /v1/web/game-config/branches/:parentEntityId/supersede */
export function handleSupersedeConfigBranch(config, match) {
    const old = requireBranch(match[1]);
    const id = PlaygroundSessionStore.getNextId();
    const replacement = branches.create({
        ...old,
        ...Object.fromEntries(Object.entries(body(config)).filter(([, v]) => v != null)),
        id,
        state: 'ACTIVE',
        versionInfo: versionInfo(id, 1, 'Superseded'),
    });
    branches.update(old.id, { state: 'ARCHIVED' });
    return [replacement, branches.getById(old.id)];
}

/** PUT /v1/web/game-config/branches/:parentEntityId/rollout — in place, no new version. */
export function handleUpdateConfigRollout(config, match) {
    const item = requireBranch(match[1]);
    return branches.update(item.id, { rolloutPercentage: body(config).rolloutPercentage ?? null });
}

/** GET /v1/web/game-config/branches/:parentEntityId/versions */
export function handleGetConfigBranchVersions(config, match) {
    return [requireBranch(match[1])];
}

/** POST /v1/web/game-config/branches/:parentEntityId/rollback/:version */
export function handleRollbackConfigBranch(config, match) {
    const item = requireBranch(match[1]);
    return branches.update(item.id, {
        state: 'DRAFT',
        versionInfo: {
            ...item.versionInfo,
            version: (item.versionInfo?.version || 1) + 1,
            versionDescription: `Rolled back to v${match[2]}`,
        },
    });
}

/** DELETE /v1/web/game-config/branches/:parentEntityId — takes its subtree with it. */
export function handleDeleteConfigBranch(config, match) {
    const item = requireBranch(match[1]);
    const doomed = [item.id];
    let added = true;
    while (added) {
        added = false;
        branches.getAll().forEach((b) => {
            const parent = branches.getAll().find((p) => lineage(p) === b.branchParentId);
            if (parent && doomed.includes(parent.id) && !doomed.includes(b.id)) {
                doomed.push(b.id);
                added = true;
            }
        });
    }
    doomed.forEach((id) => branches.delete(id));
    return { success: true };
}

/**
 * POST /v1/web/game-feature/discover — the config classification pass. Mirrors the real one: only
 * files with no area yet are touched, and each gets one stamped from its folder.
 */
export function handleDiscoverConfigSystems() {
    const AREA_BY_FOLDER = {
        economy: 'ECONOMY',
        progression: 'PROGRESSION',
        liveops: 'LIVEOPS',
        competitive: 'CORE',
        ui: 'UI',
    };
    files.getAll()
        .filter((f) => !f.system)
        .forEach((f) => {
            const folder = (f.name || '').split('/').slice(-2, -1)[0] || '';
            files.update(f.id, { system: AREA_BY_FOLDER[folder] || 'OTHER', lastModifiedDate: now() });
        });
    return { features: [] };
}

/**
 * POST /v1/web/game-config/files/:fileId/resolve-preview — what one player would receive.
 *
 * The tree walk and "the base is never a candidate" are the real rules. Segment rules are NOT
 * evaluated here — playground has no segment engine — so a targeted branch is skipped and the
 * reason says so. Windows and build gates are likewise not applied.
 */
export function handleResolveConfigPreview(config, match) {
    const fileId = match[1];
    const file = files.getById(fileId);
    const mine = branches.getAll().filter((b) => b.fileId === fileId);
    const base = mine.find((b) => b.isBase);
    const shell = {
        fileId,
        fileName: file?.name,
        format: file?.format,
        deliveredMode: 'FULL',
        layers: [],
        conflicts: [],
    };
    if (!base) {
        return { ...shell, reason: 'this config has no base, so it has no reach to serve within' };
    }

    const untargeted = (b) => !(b.segmentIds || []).length;
    const childrenOf = (id) => mine
        .filter((b) => !b.isBase && b.branchParentId === id && b.state === 'ACTIVE')
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));

    const skipped = childrenOf(lineage(base)).filter((b) => !untargeted(b)).length;
    const path = [];
    let cursor = childrenOf(lineage(base)).find(untargeted);
    while (cursor) {
        path.push(cursor);
        cursor = childrenOf(lineage(cursor)).find(untargeted);
    }

    if (!path.length) {
        return {
            ...shell,
            reason: skipped
                ? `no untargeted branch under the base matched — playground does not evaluate segment rules, so ${skipped} targeted branch(es) were skipped`
                : 'no branch under the base matched this player',
        };
    }

    const winner = path[path.length - 1];
    return {
        ...shell,
        content: winner.content,
        branchId: lineage(winner),
        branchVersion: winner.versionInfo?.version,
        baseBranchId: lineage(base),
        baseVersion: base.versionInfo?.version,
        layers: path.map((b) => ({
            branchId: lineage(b),
            name: b.name,
            isBase: false,
            kind: b.rolloutPercentage != null ? 'ROLLOUT' : 'OVERRIDE',
        })),
        reason: path.length === 1
            ? `no child matched — served the branch "${winner.name}"`
            : `matched "${winner.name}" via ${path.map((b) => b.name).join(' → ')}`,
    };
}
